import type {JsonSchema, JsonSchema4, JsonSchema7, UISchemaElement} from '@jsonforms/core'
import type {
  ControlElement,
  HorizontalLayout,
  VerticalLayout,
} from '@jsonforms/core/src/models/uischema'
import type {Optional} from 'utility-types'

import type {Build, Project} from '../../../../services/rest'
import type {OSType, PipelineId, ProjectId} from '../../../../types'
import type {PipelineSettings} from '../types'

export type NewVcsRootDto = {
  id?: string
  name?: string
  url: string
  port?: string
  branch?: string
  vcsName?: string
  branchSpecification?: string | null
  vcsProviderType?: VcsProviderType | null
  username?: string | null
  password?: string | null
  sshKey?: {
    name: string
    passphrase?: string
  }
  isPrivate?: boolean
  connectionId?: string
  isExternal?: boolean
  externalVcsRootId?: string
  isNew?: boolean
  // due to some limitations, we need both fields until backend refactoring is done
  // used in UI
  isStatusPublishingEnabled?: boolean
  // used in API requests
  statusPublishingEnabled?: boolean
  publishingSupported?: boolean
  // due to some limitations, we need both fields until backend refactoring is done
  trackPullRequests?: boolean
  pullRequestsTrackingEnabled?: boolean
  pullRequestTrackingSupported?: boolean
}

export type GetRepositoryBranchesArg = {
  url: string
  vcsName?: string
  username?: string
  isPrivate?: boolean
  branchSpecification?: string
  connectionId?: string
  parentProjectId?: ProjectId
}

export type GetParametersResolveArg = {
  id: string
  body: {
    pipeline: NewPipeline
    scope: {
      search?: string
      parameterName?: string
      context?: Record<string, string | undefined>
    }
  }
}

export type GetJobDescriptionsArg = {
  id: string
  body: NewPipeline
}

type GetJobDescriptionsJob = Record<string, string[]>

export type GetJobDescriptions = {
  jobs: Record<string, GetJobDescriptionsJob>
}

export type BuildStepDescriptions = Record<string, string>

export type GetBuildStepDescriptionArg = {
  id: string
  body: NewPipeline
}

export type GetBuildStepDescription = {steps: BuildStepDescriptions} | BuildStepDescriptions

export type PipelineParametersResolve = {
  name: string
  type: 'KNOWN_PASSWORD' | 'KNOWN_PARAMETER' | 'SUSPECTED_PARAMETER'
  values: string[]
}

export type RepositoryTestConnectionArg = {
  parentProjectId?: ProjectId
  body: {
    url: string
    connectionId?: string
    username?: string | null
    password?: string | null
    vcsName?: string
    sshKey?: {
      name: string
      passphrase?: string
    }
    isPrivate?: boolean // I added this type since it's the one we use and according to git history it was here, but for some reason it disappeared
  }
}

export type VcsRootDto = NewVcsRootDto & {
  id: string
}
type ProjectSshKey = {
  name: string
  encrypted: boolean
}
export type ProjectSshKeys = {
  sshKey: ProjectSshKey[]
}

export type NewTriggerDto = {
  type: string
  parameters?: Record<string, string>
  disabled?: boolean
  triggerBranches?: boolean
  triggerPullRequests?: boolean
}

type TriggerDto = Required<NewTriggerDto>

export type NewIntegrationDto = {
  id?: string
  type: string
  parameters: Record<string, string | null>
  projectExternalId: string
}

type IntegrationDto = Required<NewIntegrationDto>

export enum ConfirmationDecision {
  OVERRIDE = 'override',
  IMPORT = 'import',
}

export type Event = {
  type: string
  enabled: boolean
}

export type PersonalNotificationsDto = {
  ownChangesOnly: boolean
  events: Event[]
}

export type VersionedSettingsFormat = 'kotlin' | 'xml' | 'pipelines-yaml'

export type VersionedSettingsDto = {
  storedInRepo?: boolean
  filename?: string | null
  format?: VersionedSettingsFormat | null
  confirmationDecision?: ConfirmationDecision
}

export type NotificationDto = {
  id?: string
  type: string
  enabled: boolean
  branchSpecification: string
  emails: string[]
  users: string[]
  events: Event[]
}

export type NewPipelineDto = {
  yaml: string
  name: string
  vcsRoot?: NewVcsRootDto | null
  triggers?: NewTriggerDto[]
  additionalVcsRoots: NewVcsRootDto[]
  integrations?: NewIntegrationDto[]
  versionedSettings?: VersionedSettingsDto
  notifications: NotificationDto[]
}

export type NewPipeline = Optional<NewPipelineDto, 'yaml'> & {
  settings: PipelineSettings
}

export type PipelineDto = {
  id: PipelineId
  name: string
  pipelineVersion?: string | null
  yaml: string
  vcsRoot: VcsRootDto | null
  additionalVcsRoots: VcsRootDto[]
  triggers: TriggerDto[]
  integrations: IntegrationDto[]
  versionedSettings?: VersionedSettingsDto
  notifications: NotificationDto[]
}

export type Pipeline = Optional<PipelineDto, 'yaml'> & {
  settings: PipelineSettings
}

export type GetPipelineByIdArg = {
  id: PipelineId
  branch?: string
}

export type CreateNewPipelineArg = {
  parentProjectId?: string
  body: NewPipeline
}

export type CommitDetails = {
  branch?: string | null
  message: string
}

export type GetPipelineBranchesArg = {
  pipelineId: PipelineId
  locator?: string
  fields?: string
}

export type UpdatePipelineArg = {
  id: PipelineId
  pipeline: NewPipeline
  commitDetails?: CommitDetails
}

export type ValidatePipelineArg = {
  pipelineId: PipelineId
  body: NewPipeline
}

export type DeletePipelineArg = {
  id: PipelineId
  cacheUpdateDelay?: number
}

export type Optimization = {
  name: string
  value: number
  type: OptimizationType
}

export enum Optimizations {
  PARALLEL_TESTING = 'parallelTests',
  REUSING_JOBS = 'reusedJobs',
  DEPENDENCY_CACHE = 'dependencyCache',
}

export type PossibleOptimization = {
  name: string
  expectedValue?: string
}

export type RunDuration = {
  number: number
  duration: number
  optimized?: number
  canceled?: boolean
}

export type RunDurations = {
  queuedDate: string
  startedDate?: string
  finishedDate?: string
  estimatedDuration?: number
  optimizedDuration?: number
  previousRunDuration?: number
  appliedOptimizations?: Optimization[]
  possibleOptimizations?: PossibleOptimization[]
}

export type PipelineDurations = {
  totalDuration: number
  optimizedDuration?: number
  optimizations?: Optimization[]
  runs?: RunDuration[]
}

export type CheckVersionedSettingsArg = {
  vcsRootId: string
  filename?: string
  branch?: string
}

export type GetEffectiveVersionedSettingsConfigArg = {
  projectId: ProjectId
}

type SynchronizationMode = 'useParentProjectSettings' | 'disabled' | 'enabled'
type BuildSettingsMode = 'alwaysUseCurrent' | 'useCurrentByDefault' | 'useFromVCS'
type ImportDecision = 'overrideInVCS' | 'importFromVCS'

export type EffectiveVersionedSettingsConfig = {
  config?: {
    format?: VersionedSettingsFormat
    storeSecureValuesOutsideVcs?: boolean
    applyChangesInDependenciesAndVcsSettings?: boolean
    portableDsl?: boolean
    vcsRootId?: string
    synchronizationMode?: SynchronizationMode
    showSettingsChanges?: boolean
    buildSettingsMode?: BuildSettingsMode
    settingsPath?: string
    importDecision?: ImportDecision
    allowUIEditing?: boolean
  }
  project?: Project
} | null

type JsonSchemaExtendedDefs = Record<
  string,
  {
    enumNames: string[]
    enumDescription?: string[]
    enumValues?: string[]
  }
>

type JsonSchemaDefs = {
  $defs?: JsonSchema['definitions'] & JsonSchemaExtendedDefs
}

export type UiSchemaElements = (VerticalLayout | HorizontalLayout | {type: string}) & {
  elements: (ControlElement | UISchemaElement)[]
  title?: string
  description?: string
}

export type PipelineRunnersJsonFromXMLs = Record<
  string,
  {
    jsonSchema: JsonSchema4 & JsonSchema7 & JsonSchemaDefs
    uiSchema?: UiSchemaElements
  }
>

export type PipelineYamlSchema = JsonSchema & {
  definitions: Record<string, any>
  properties: Record<string, any>
}

export type GeneratePipelineSchemaArg = {
  pipelineId: PipelineId
  body: NewPipeline
}

export type PipelineFeaturesJsonFromXMLs = Record<
  string,
  {
    jsonSchema: JsonSchema4 & JsonSchema7 & JsonSchemaDefs
    uiSchema?: UiSchemaElements
    allowMultiple?: boolean
  }
>

export type PipelineProviderJdk = {
  displayName: string
  parameterReference: string
}

export enum OptimizationType {
  DURATION = 'duration',
  SIZE = 'size',
}

export enum VcsProviderType {
  GITHUB = 'GitHub',
  GHE = 'GHE',
  GITHUBAPP = 'GitHubApp',
  GITLAB_COM = 'GitLabCom',
  GITLAB_CE_OR_EE_COM = 'GitLabCEorEE',
  BITBUCKET_CLOUD = 'BitBucketCloud',
  BITBUCKET_SERVER = 'BitbucketServer',
  AZURE_DEV_OPS = 'AzureDevOps',
  TFS = 'tfs', // Azure
  // client only options
  ANY = 'Any',
  NEW_CONNECTION = 'NewConnection',
  EXISTING_VCS_ROOT = 'ExistingVcsRoot',
  CONNECT_BY_REPOSITORY_URL = 'ConnectByRepositoryURL',
  WITHOUT_REPOSITORY = 'WithoutRepository',
  TEMPLATE = 'Template',
}
export enum VcsProviderCapability {
  REPOS_PAGING = 'REPOS_PAGING',
  REPOS_PAGE_SIZE = 'REPOS_PAGE_SIZE',
  REPOS_SEARCH = 'REPOS_SEARCH',
  COMMON = 'COMMON',
}

export enum BuildFeatureType {
  CSP = 'commit-status-publisher',
  PULL_REQUESTS = 'pullRequests',
}

export const TYPE_TO_LINK_HASH = new Map<string, string>([
  ['GitHub', 'github'],
  ['GHE', 'github'],
  ['GitHubApp', 'githubApp'],
  ['GitLabCom', 'gitlab'],
  ['GitLabCEorEE', 'gitlab'],
  ['AzureDevOps', 'AzureDevOps'],
  ['tfs', 'tfs'],
  ['JetBrains Space', 'space'],
  ['BitBucketCloud', 'bitbucket'],
  ['BitbucketServer', 'bitbucketserver'],
])

export type AvailableVcsProviderType = {
  type: string
  displayName: string
  description?: string | null
  recommended?: boolean
}
export type GroupedAvailableVcsProviders = {
  types: AvailableVcsProviderType[]
  category: string | null
}

export type VcsProvider = {
  connectionId?: string
  password?: string
  username?: string
  type?: VcsProviderType
  displayName?: string
  capabilities?: VcsProviderCapability[]
  supportedBuildFeatures?: BuildFeatureType[]
  description?: string
  pullRequestTriggersSupported?: boolean
}

export type VcsRepository = {
  repositoryName: string
  httpCloneUrl: string
  sshCloneUrl: string | null
  htmlUrl: string
  defaultBranch?: string | null
  description: string | null
  language: string | null
  isPrivate: boolean
  isFork: boolean
}

export type GetRepositoriesResponse = {
  values: VcsRepository[]
  hasMore: boolean
  nextPage: number | null
  totalCount: number | null
}

export type GetRepositoriesArg = {
  connectionId: string
  parentProjectId?: ProjectId
  q?: string
  page?: number
  pageSize?: number
}

export type GetPipelineRunVcsRootsArg = {
  pipelineId: PipelineId
  runId: number
}

export type GetProjectSshKeysArg = {
  projectId: string
}

export type GetPipelineSshKeysArg = {
  pipelineId: PipelineId
}

export type ParameterWithType = {
  name: string
  value: string
  type?: string | {rawValue?: string}
}

export type ProjectParametersDto = {
  id: string
  name: string
  parentProjectId?: string
  parametersWithType?: ParameterWithType[]
}

export type ProjectWithAncestorsDto = {
  id: string
  name: string
  parentProjectId?: ProjectId | undefined
  parameters?: Record<string, string>
  parametersWithType?: ParameterWithType[]
  ancestorProjects?: {
    project: ProjectParametersDto[]
  }
}

export type GetParentParametersArg = {
  parentProjectId: string
}

export interface AgentCompatibilitiesArg {
  pipelineId: PipelineId
  jobId?: string
  body: NewPipeline
}

export type GetCompatibilityAgentsQueuedJobArg = {
  pipelineId: PipelineId
  buildId: number
}

type JobAgentCompatibilities = {
  compatibilities: Array<AgentCompatibility>
}

export type JobMapAgentCompatibilities = Record<string, JobAgentCompatibilities>

export type AgentCompatibilities = JobAgentCompatibilities

export interface AgentCompatibility {
  unmetReqirements: Array<AgentRequirement>
  unresolvedParameters: Array<{origin: string; parameter: string}>
  invalidRunnerParameters: Array<{
    parameter: string
    reason: string
  }>
  compatible: boolean
  agent: Agent
}

interface AgentRequirement {
  parameter: string
  predicate: string
  name: string
  value: string
}

interface Agent {
  osType: Uppercase<OSType> | 'MAC' | null
  name: string
  id: number
  type: string | 'AGENT' | 'CLOUD_IMAGE'
}

export type DebugResponse = {
  jobs: {
    count?: number
    job?: {build: Build}[]
  }
  build: Build
}

export type DebugArg = {
  pipelineId: PipelineId
  body: {
    branch?: string
    debug?: Record<
      string,
      {
        breakpoints: string[]
      }
    >
    yaml?: string
  }
  fields: string
}

export type NotificationsArg = {
  pipelineId: PipelineId
}

export enum PipelineErrorType {
  YAML_VALIDATION_PROBLEM = 'yamlValidationProblem',
  YAML_COMMIT_FAILED = 'yamlCommitFailed',
  UNCATEGORIZED = 'uncategorized',
}

export type PipelineErrorDto = {
  message: string
  type: PipelineErrorType
  stackTrace?: string | null
  additionalMessage?: string | null
}
